import { decryptAndParseJson } from '../util/crypto'
import { BUYONLINE_START_URL, BUYONLINE_AGENT_AUTH_URL } from '../util/api'
import { QuoteService } from './quote-service'
import { getEmbeddedContext } from '../util/embedded'

export class ApplicationService {
  /**
   * Create and submit a hidden form to start the application process
   */
  static submitApplication(
    quoteData: any,
    shoppingCart: any[],
    skipVision: boolean = false,
    skipDental: boolean = false,
    campaignId: string = '',
    producerSession?: any
  ): void {
    const formHtml = this.createStartForm(
      'issApp',
      true,
      quoteData,
      shoppingCart,
      skipVision,
      skipDental,
      campaignId,
      producerSession
    )

    if (!formHtml) {
      console.error('Failed to create application form')
      return
    }

    // Create temporary div, append form, submit, and remove
    const tempDiv = document.createElement('div')
    tempDiv.innerHTML = formHtml
    document.body.appendChild(tempDiv)
    const form = tempDiv.querySelector('form') as HTMLFormElement
    if (form) {
      form.submit()
      document.body.removeChild(tempDiv)
    }
  }

  /**
   * Create the hidden form HTML for application submission
   */
  private static createStartForm(
    prefix: string,
    topLevel: boolean,
    quoteData: any,
    shoppingCart: any[],
    skipVision: boolean,
    skipDental: boolean,
    campaignId: string,
    producerSession?: any
  ): string {
    if (!quoteData) {
      return ''
    }

    // Filter shopping cart based on skip flags
    let filteredCart = [...shoppingCart]
    if (skipVision) {
      filteredCart = filteredCart.filter((p: any) => !p.planId.includes('VIS'))
    }
    if (skipDental) {
      filteredCart = filteredCart.filter((p: any) => !p.planId.includes('IDTL'))
    }

    if (filteredCart.length === 0) {
      return ''
    }

    // Determine plan type
    let type: string
    if (topLevel) {
      type = filteredCart[0].planId
    } else if (filteredCart.length > 1) {
      type = 'IDTL'
    } else {
      return ''
    }

    const plan = this.getPlanByPlanType(type, filteredCart, quoteData)
    if (!plan) {
      return ''
    }

    // Prefer forms from quoteData, else fall back to cached agent forms
    const forms =
      (quoteData as any)?.forms || QuoteService.getCachedAgentForms() || null
    const quoteFormEnc = forms?.quoteForm || ''
    // Decrypt and parse quote form (already returns an object)
    const qForm = quoteFormEnc ? decryptAndParseJson(quoteFormEnc) : {}

    // When the URL contains a `quote` param, the decoded payload carries address
    // and SSN fields that may not be present in the backend-returned qForm.
    // Fall back to that decoded data so the form submission always includes them.
    let quoteParamData: any = {}
    try {
      const quoteParam = new URLSearchParams(window.location.search).get(
        'quote'
      )
      if (quoteParam) {
        quoteParamData = JSON.parse(atob(quoteParam))
      }
    } catch {
      // non-blocking
    }

    const quoteId =
      quoteData.quoteApplication?.bolApplication?.quoteId ||
      quoteData.quoteApplication?.quoteId ||
      new URLSearchParams(window.location.search).get('quoteId') ||
      ''
    const bolApp = quoteData.quoteApplication?.bolApplication || {}
    const county =
      bolApp.county || qForm.countyCode || quoteParamData.countyCode || ''
    const effectiveDate = qForm.effectiveDate || ''
    const phone = qForm.phone || quoteParamData.phone || ''
    const email = qForm.email || quoteParamData.email || ''
    const primarySsn = qForm.primarySsn || quoteParamData.primarySsn || ''
    const selfQuestion = qForm.selfQuestion || quoteParamData.selfQuestion || ''
    const street1 = qForm.street1 || quoteParamData.street1 || ''
    const street2 = qForm.street2 || quoteParamData.street2 || ''
    const city = qForm.city || quoteParamData.city || ''
    const state = qForm.state || bolApp.state || quoteParamData.state || 'NC'
    const zip = qForm.zip || quoteParamData.zip || ''
    const referralSource = bolApp.referralSource || 'DIRECT'

    // Get plan name
    let planName: string
    if (plan.planId.includes('VIS')) {
      planName = plan.plan.description
    } else {
      const jsonDetails = JSON.parse(plan.plan.details.jsonDetails)
      planName = jsonDetails.PRODUCT_DESCRIPTION
      const subHeader = jsonDetails.SUBHEADER
      const planYear = effectiveDate.split('/')[2]
      if (planYear === '2026' && !planName.includes(' - ')) {
        planName = planName.concat(' - ').concat(subHeader)
        if (planName.endsWith(' Plan')) {
          planName = planName.substring(0, planName.indexOf(' Plan'))
        }
      }
    }

    // Build form HTML

    const isPabol =
      !!quoteData?.quoteApplication?.bolApplication?.isPABOL ||
      qForm?.deliveryMethod === 'p' ||
      quoteParamData?.deliveryMethod === 'p'

    const startAction =
      isPabol && new URLSearchParams(window.location.search).get('userID')
        ? BUYONLINE_AGENT_AUTH_URL()
        : BUYONLINE_START_URL()

    let formHtml =
      (topLevel ? `<form method='post' action='${startAction}'>` : '') +
      `<input type="hidden" name="newApplication" value="Y">` +
      `<input type="hidden" name="${prefix}.enrollmentType" value="NEW">` +
      (quoteId
        ? `<input type="hidden" name="${prefix}.quoteId" value="${quoteId}">`
        : '') +
      this.addApplicantDetails(
        prefix,
        quoteData.quoteApplication.applicants &&
          quoteData.quoteApplication.applicants.length > 0
          ? quoteData.quoteApplication.applicants
          : quoteData.quoteApplication.dependents || [],
        plan.plan,
        plan.plan.facetsProductId || plan.plan.id
      ) +
      (primarySsn
        ? `<input type="hidden" name="${prefix}.applicants[0].ssn" value="${primarySsn}">`
        : '') +
      (selfQuestion
        ? `<input type="hidden" name="${prefix}.selfQuestion" value="${selfQuestion}">`
        : '') +
      `<input type="hidden" name="${prefix}.referralSource" value="${referralSource}">` +
      (phone
        ? `<input type="hidden" name="${prefix}.phoneNumber" value="${phone}">`
        : '') +
      (email
        ? `<input type="hidden" name="${prefix}.emailAddress" value="${email}">`
        : '') +
      (campaignId
        ? `<input type="hidden" name="${prefix}.campaignCode" value="${campaignId}">`
        : '') +
      (qForm.street1 || street1
        ? `<input type="hidden" name="${prefix}.physicalAddress.streetLine1" value="${street1}">`
        : '') +
      (street2
        ? `<input type="hidden" name="${prefix}.physicalAddress.streetLine2" value="${street2}">`
        : '') +
      (qForm.city || city
        ? `<input type="hidden" name="${prefix}.physicalAddress.city" value="${city}">`
        : '') +
      `<input type="hidden" name="${prefix}.physicalAddress.state" value="${state}">` +
      (qForm.zip || zip
        ? `<input type="hidden" name="${prefix}.physicalAddress.zipcode" value="${zip}">`
        : '') +
      (county
        ? `<input type="hidden" name="${prefix}.physicalAddress.county" value="${county}">`
        : '') +
      (!skipDental && topLevel && filteredCart.length > 1
        ? this.createStartForm(
            'issApp.dentalApplication',
            false,
            quoteData,
            filteredCart,
            skipVision,
            skipDental,
            campaignId,
            producerSession
          )
        : '') +
      `<input type="hidden" name="${prefix}.formVersion" value="${this.getFormVersion(effectiveDate, plan.planId)}">` +
      `<input type="hidden" name="${prefix}.planInformation.selectedPlanName" value="${planName}">` +
      `<input type="hidden" name="${prefix}.planInformation.selectedProductFamily" value="${plan.plan.description}">` +
      `<input type="hidden" name="${prefix}.planInformation.selectedPlanId" value="${plan.plan.facetsProductId || plan.plan.id}">` +
      `<input type="hidden" name="${prefix}.planInformation.requestedEffectiveDate" value="${effectiveDate}">` +
      `<input type="hidden" name="${prefix}.planInformation.planYear" value="${effectiveDate.split('/')[2]}">` +
      `<input type="hidden" name="${prefix}.planInformation.monthlyPremium" value="${
        plan.planId.includes('VIS')
          ? plan.plan.planRate.monthlyPremium
          : plan.plan.rate.monthlyPremium
      }">` +
      this.addAgentInfo(
        prefix,
        forms?.producerInfoJ || '',
        producerSession,
        referralSource
      ) +
      (topLevel ? '</form>' : '')

    // Remove duplicate agent inputs
    formHtml = formHtml.replace(
      /(<input[^>]+name=['"]verifyAgent\.loggedInAgent['"][^>]*>)([\s\S]*?)\1+/g,
      '$1$2'
    )
    formHtml = formHtml.replace(
      /(<input[^>]+name=['"]verifyAgent\.agentRedirectType['"][^>]*>)([\s\S]*?)\1+/g,
      '$1$2'
    )

    return formHtml
  }

  /**
   * Add applicant details as hidden form inputs
   */
  private static addApplicantDetails(
    prefix: string,
    applicants: any[],
    plan: any,
    planId: string
  ): string {
    let applicantHtml = ''
    applicants.forEach((applicant, index) => {
      // Fallback mapping for dependents array or missing fields
      const rel = applicant.relationship || applicant.dependentType || 'Primary'
      const dob = applicant.dateOfBirth || applicant.birthday || ''
      const isPrimary = rel === 'Self' || rel === 'Primary'
      const firstName = applicant.firstName || ''
      const lastName = applicant.lastName || ''
      const ssn = applicant.ssn || ''
      const handicap = applicant.handicap || false
      const isDental = applicant.isDental ?? ''
      const isVision = applicant.isVision ?? ''
      const rate = this.findApplicantRate(plan, applicant.sequenceId)
      applicantHtml +=
        `<input type="hidden" name="${prefix}.applicants[${index}].isPrimary" value="${isPrimary ? 'true' : 'false'}">` +
        `<input type="hidden" name="${prefix}.applicants[${index}].dateOfBirth" value="${this.convertToIssDate(dob)}">` +
        `<input type="hidden" name="${prefix}.applicants[${index}].relationshipToPrimary" value="${rel.toUpperCase()}">` +
        (firstName
          ? `<input type="hidden" name="${prefix}.applicants[${index}].firstName" value="${firstName}">`
          : '') +
        (lastName
          ? `<input type="hidden" name="${prefix}.applicants[${index}].lastName" value="${lastName}">`
          : '') +
        (ssn
          ? `<input type="hidden" name="${prefix}.applicants[${index}].ssn" value="${ssn}">`
          : '') +
        `<input type="hidden" name="${prefix}.applicants[${index}].enrollPlanId" value="${planId}">` +
        `<input type="hidden" name="${prefix}.applicants[${index}].applicantId" value="${index}">` +
        `<input type="hidden" name="${prefix}.applicants[${index}].monthlyPremium" value="${rate}">` +
        (handicap
          ? `<input type="hidden" name="${prefix}.applicants[${index}].handicaped" value="${handicap}">`
          : '') +
        (index !== 0
          ? `<input type="hidden" name="issApp.applicants[${index}].isDental" value="${isDental}">`
          : '') +
        (index !== 0
          ? `<input type="hidden" name="${prefix}.applicants[${index}].isVision" value="${isVision}">`
          : '')
    })
    return applicantHtml
  }

  /**
   * Find applicant rate from plan
   */
  private static findApplicantRate(plan: any, sequenceId: string): string {
    let rate = ''
    plan.applicantRate?.forEach((appRate: any) => {
      if (appRate.personId === sequenceId) {
        rate = appRate.monthlyPremium.toString()
      }
    })
    return rate
  }

  /**
   * Add agent information as hidden form inputs
   */
  private static addAgentInfo(
    prefix: string,
    producerInfo: any,
    producerSession?: any,
    referralSource?: string
  ): string {
    const { userId, pnumber: pnumParam, isEmbedded } = getEmbeddedContext()

    // Skip agent information for direct shopper flow unless this is embedded.
    if (referralSource === 'DIRECT' && !isEmbedded) {
      return ''
    }

    let producer: any = null
    try {
      if (producerInfo) {
        producer = decryptAndParseJson(producerInfo)
      } else if (producerSession) {
        producer = producerSession
      }
    } catch (e) {
      producer = producerSession || null
    }

    // Prepare values with safe fallbacks
    const agentPhone =
      producer?.workPhoneNumber || producer?.producerPhone || ''
    const agentEmail = producer?.email || ''
    const agentInternal =
      producer?.producerNumber || producer?.producerPNumber || pnumParam || ''
    const agentFullName =
      (
        (producer?.firstName || '') +
        (producer?.lastName ? ' ' + producer?.lastName : '')
      ).trim() ||
      producer?.fullName ||
      ''
    const agencyName =
      producer?.individualAgencyName || producer?.corpName || ''

    let html = ''
    html += `<input type="hidden" name="${prefix}.agentInformation.agentPhone" value="${agentPhone}">`
    html += `<input type="hidden" name="${prefix}.agentInformation.agentEmail" value="${agentEmail}">`
    html += `<input type="hidden" name="${prefix}.agentInformation.agentInternalNumber" value="${agentInternal}">`
    html += `<input type="hidden" name="${prefix}.agentInformation.agentFullName" value="${agentFullName}">`
    html += `<input type="hidden" name="${prefix}.agentInformation.agencyName" value="${agencyName}">`
    const loggedInAgentValue = userId || agentInternal || ''
    html += `<input type="hidden" name="verifyAgent.loggedInAgent" value="${loggedInAgentValue}">`
    if (userId || isEmbedded) {
      html += `<input type="hidden" name="verifyAgent.agentRedirectType" value="startNew">`
    }

    return html
  }

  /**
   * Get plan by plan type from shopping cart
   */
  private static getPlanByPlanType(
    type: string,
    shoppingCart: any[],
    quoteData: any
  ): any {
    const cartItem = shoppingCart.find((p) => p.planId.includes(type))
    if (!cartItem) {
      return null
    }

    // Find the full plan details from quoteData
    if (type.includes('VIS')) {
      const plan = quoteData.individualPlans?.find(
        (p: any) => p.id === cartItem.planId || p.planId === cartItem.planId
      )
      return plan ? { planId: cartItem.planId, plan } : null
    } else {
      // Dental plans may be sourced from crossSalePlans or jsonPlans depending on flow
      const plan =
        quoteData.crossSalePlans?.find(
          (p: any) => p.planId === cartItem.planId
        ) || quoteData.jsonPlans?.find((p: any) => p.planId === cartItem.planId)
      return plan ? { planId: cartItem.planId, plan } : null
    }
  }

  /**
   * Convert date to ISS format (YYYY-MM-DD)
   */
  private static convertToIssDate(date: string): string {
    // Convert YYYY-MM-DD to MM/DD/YYYY
    if (date.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = date.split('-')
      return `${month}/${day}/${year}`
    }
    // Already in MM/DD/YYYY format
    if (date.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
      return date
    }
    return date
  }

  /**
   * Get form version based on effective date and product type
   */
  private static getFormVersion(effDate: string, product: string): string {
    const yearMatch = (effDate || '').match(/(\d{4})$/)
    const effectiveYear = yearMatch
      ? Number(yearMatch[1])
      : new Date().getFullYear()
    const formYear = Number.isFinite(effectiveYear)
      ? effectiveYear - 1
      : new Date().getFullYear() - 1
    const productPrefix = product.includes('VIS') ? 'VISION' : 'DENTAL'

    return `${productPrefix}_JUN${formYear}`
  }
}
